import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  Building2,
  BadgeCheck,
  Star,
  ShieldOff,
  Sparkles,
  Trash2,
  RefreshCw,
  Loader2,
} from "lucide-react";

const CAT_AR = {
  showroom: "معرض",
  insurance: "تأمين",
  inspection: "فحص",
  transport: "نقل",
  maintenance: "صيانة",
  car_care: "عناية بالسيارات",
  financing: "تمويل",
  parts: "قطع غيار",
  tires: "إطارات",
  rental: "تأجير",
  delivery: "نقل وشحن",
};

const STAT_META = {
  total:     { ar: "الإجمالي", en: "Total",     color: "#0a0a0a" },
  pending:   { ar: "المعلّق",  en: "Pending",   color: "#c9a55c" },
  verified:  { ar: "الموثّق",  en: "Verified",  color: "#0a7d3a" },
  suspended: { ar: "الموقوف",  en: "Suspended", color: "#b42318" },
};

/**
 * PartnersTab — iter257 refresh.
 *
 * Real data only from /api/partners/admin/all. Every button hits the
 * live PATCH/DELETE endpoints and refetches counts + list so numbers
 * always match the DB. Adds:
 *   - Manual refresh (↻) with loading + error surfacing
 *   - Explicit loading state while (re)fetching
 *   - Per-row busy state so double-taps don't spam the backend
 *   - Full Arabic labels for the four stat cards
 *   - Toast on every action's failure (previously only on delete)
 */
export default function PartnersTab() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [data, setData] = useState({ items: [], counts: {} });
  const [filter, setFilter] = useState({ status: "", category: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.category) params.category = filter.category;
      const { data: res } = await apiClient.get("/partners/admin/all", { params });
      setData(res || { items: [], counts: {} });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "network error";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (p, patch, successMsg) => {
    if (busyRowId) return;
    setBusyRowId(p.id);
    try {
      await apiClient.patch(`/partners/admin/${p.id}`, patch);
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(isAr ? "تعذّر تنفيذ الإجراء" : "Action failed");
    } finally {
      setBusyRowId(null);
    }
  };

  const remove = async (p) => {
    const label = p.name_ar || p.name_en || p.id;
    const ok = window.confirm(
      isAr
        ? `سيتم حذف الشريك "${label}" نهائياً. متابعة؟`
        : `This will permanently delete partner "${label}". Continue?`
    );
    if (!ok || busyRowId) return;
    setBusyRowId(p.id);
    try {
      await apiClient.delete(`/partners/admin/${p.id}`);
      toast.success(isAr ? "تمّ الحذف" : "Deleted");
      await load();
    } catch (err) {
      toast.error(isAr ? "تعذّر الحذف" : "Delete failed");
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <div data-testid="admin-partners" className="space-y-6">
      {/* Header + refresh */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#8a6f2e]" />
          {isAr ? "الشركاء" : "Partners"}
        </h2>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold border border-[#0a0a0a]/15 bg-white hover:bg-[#0a0a0a] hover:text-white transition-colors disabled:opacity-60"
          data-testid="partners-refresh"
          aria-label={isAr ? "تحديث" : "Refresh"}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {isAr ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["total", "pending", "verified", "suspended"].map((k) => (
          <div key={k} className="surface rounded-xl p-4" data-testid={`partners-stat-${k}`}>
            <div
              className="text-xs uppercase tracking-widest"
              style={{ color: STAT_META[k].color }}
            >
              {isAr ? STAT_META[k].ar : STAT_META[k].en}
            </div>
            <div className="font-display text-3xl mt-1 text-[#0a0a0a]">
              {loading ? "…" : (data.counts?.[k] || 0)}
            </div>
          </div>
        ))}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="rounded-xl bg-red-50 border border-red-300 px-4 py-3 text-sm text-red-800 flex items-center justify-between gap-3"
          data-testid="partners-error"
        >
          <span>{isAr ? "تعذّر تحميل البيانات: " : "Failed to load data: "}<code className="text-xs">{error}</code></span>
          <button onClick={load} className="text-xs font-bold underline">
            {isAr ? "إعادة المحاولة" : "Retry"}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          className="input-dark max-w-xs"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          data-testid="partners-filter-status"
        >
          <option value="">{isAr ? "كل الحالات" : "All"}</option>
          <option value="pending">{isAr ? "قيد التوثيق" : "Pending"}</option>
          <option value="verified">{isAr ? "موثّقون" : "Verified"}</option>
          <option value="suspended">{isAr ? "موقوفون" : "Suspended"}</option>
        </select>
        <select
          className="input-dark max-w-xs"
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          data-testid="partners-filter-cat"
        >
          <option value="">{isAr ? "كل الفئات" : "All categories"}</option>
          {Object.entries(CAT_AR).map(([k, v]) => (
            <option key={k} value={k}>{isAr ? v : k}</option>
          ))}
        </select>
      </div>

      {/* Rows */}
      <div className="space-y-3">
        {loading && data.items.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center text-[#6b6b6b] inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" />
            {isAr ? "جاري التحميل…" : "Loading…"}
          </div>
        ) : data.items.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center text-[#6b6b6b]">
            {isAr ? "لا شركاء بعد." : "No partners yet."}
          </div>
        ) : (
          data.items.map((p) => {
            const rowBusy = busyRowId === p.id;
            return (
              <div key={p.id} className="surface rounded-xl p-4" data-testid={`admin-partner-${p.id}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg">{p.name_ar || p.name_en}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0a0a0a]/[0.06] text-[#3a3a3a]">
                        {CAT_AR[p.category] || p.category}
                      </span>
                      {p.verified && <BadgeCheck className="w-4 h-4 text-green-600" />}
                      {p.featured && <Star className="w-4 h-4 text-[#c9a55c] fill-current" />}
                      {p.suspended && <ShieldOff className="w-4 h-4 text-red-600" />}
                      {p.is_new && !p.verified && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {isAr ? "جديد" : "New"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6b6b6b] mt-1">
                      {p.city} · {p.phone} · ⭐ {p.rating_avg?.toFixed?.(1) || "—"} ({p.rating_count || 0}) · 📦 {p.orders_count || 0}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap" aria-busy={rowBusy}>
                    <button
                      onClick={() =>
                        update(
                          p,
                          { verified: !p.verified },
                          isAr
                            ? p.verified ? "أُلغي التوثيق" : "تمّ التوثيق"
                            : p.verified ? "Unverified" : "Verified"
                        )
                      }
                      disabled={rowBusy}
                      className="btn-ghost text-xs disabled:opacity-50"
                      data-testid={`partner-verify-${p.id}`}
                    >
                      <BadgeCheck className="w-3 h-3" />
                      {p.verified
                        ? (isAr ? "إلغاء التوثيق" : "Unverify")
                        : (isAr ? "توثيق" : "Verify")}
                    </button>
                    <button
                      onClick={() =>
                        update(
                          p,
                          { featured: !p.featured },
                          isAr
                            ? p.featured ? "أُلغي التمييز" : "تمّ التمييز"
                            : p.featured ? "Unfeatured" : "Featured"
                        )
                      }
                      disabled={rowBusy}
                      className="btn-ghost text-xs disabled:opacity-50"
                      data-testid={`partner-feature-${p.id}`}
                    >
                      <Sparkles className="w-3 h-3" />
                      {p.featured
                        ? (isAr ? "إلغاء التمييز" : "Unfeature")
                        : (isAr ? "تمييز" : "Feature")}
                    </button>
                    <button
                      onClick={() =>
                        update(
                          p,
                          { suspended: !p.suspended },
                          isAr
                            ? p.suspended ? "أُعيد التنشيط" : "أُوقف الشريك"
                            : p.suspended ? "Reactivated" : "Suspended"
                        )
                      }
                      disabled={rowBusy}
                      className="btn-ghost text-xs text-red-700 border-red-300 disabled:opacity-50"
                      data-testid={`partner-suspend-${p.id}`}
                    >
                      {p.suspended
                        ? (isAr ? "إعادة تنشيط" : "Reactivate")
                        : (isAr ? "إيقاف" : "Suspend")}
                    </button>
                    <button
                      onClick={() => remove(p)}
                      disabled={rowBusy}
                      className="btn-ghost text-xs text-red-800 border-red-400 hover:bg-red-50 disabled:opacity-50"
                      data-testid={`partner-delete-${p.id}`}
                      title={isAr ? "حذف نهائي" : "Permanent delete"}
                    >
                      <Trash2 className="w-3 h-3" />
                      {isAr ? "حذف" : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
